import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { RefreshCw, CheckCircle2, AlertTriangle, Star, Sparkles, MapPin, ExternalLink } from 'lucide-react';
import api from '../../services/api';

// Gestor de Tráfego — aba Google › Perfil da Empresa (Google Business Profile). Usa a conexão Google do SEO.
// Enquanto o Google não aprova o pedido de acesso à API (cota 0), a tela mostra o aviso e o esqueleto vazio.

const erroMsg = (err) => err?.response?.data?.error || err?.message;
const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR'));
const fmtDia = (s) => { const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}` : s; };
const variacao = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null);
const DIA = { MONDAY: 'seg', TUESDAY: 'ter', WEDNESDAY: 'qua', THURSDAY: 'qui', FRIDAY: 'sex', SATURDAY: 'sáb', SUNDAY: 'dom' };

const AVISO = {
  aguardando_aprovacao: {
    titulo: 'Depende da API do Google — aguardando aprovação',
    texto: 'A conexão está pronta (permissão do Perfil da Empresa concedida), mas o Google libera essas APIs com cota 0 até aprovar o pedido de acesso do projeto (formulário "GBP API access request"). A aprovação costuma levar alguns dias. Quando sair, esta tela passa a funcionar sozinha — não precisa reconectar.',
  },
  api_desligada: {
    titulo: 'Depende da API do Google — API desativada no projeto',
    texto: 'Ative no Google Cloud (projeto do login do SEO) as APIs: My Business Account Management, My Business Business Information, Business Profile Performance e Google My Business (avaliações).',
  },
  reconectar: {
    titulo: 'Falta a permissão do Perfil da Empresa',
    texto: 'Em Google › SEO e Analytics clique em "Reconectar" e marque a permissão de gerenciar o Perfil da Empresa.',
  },
  nao_conectado: {
    titulo: 'Google não conectado',
    texto: 'Conecte o Google em Google › SEO e Analytics — o Perfil da Empresa usa a mesma conexão.',
  },
  erro: { titulo: 'O Google respondeu com erro', texto: '' },
};

function Kpi({ label, valor, antes }) {
  const v = antes != null && valor != null ? variacao(valor, antes) : null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-800 mt-1">{fmtNum(valor)}</p>
      {v != null && <p className={`text-xs mt-1 ${v > 0 ? 'text-green-600' : 'text-red-600'}`}>{v > 0 ? '+' : ''}{v}% vs período anterior</p>}
    </div>
  );
}

const Estrelas = ({ n }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= (n || 0) ? 'text-yellow-500 fill-yellow-400' : 'text-gray-300'} />)}</span>
);

function EscolherLocal() {
  const qc = useQueryClient();
  const locais = useQuery({ queryKey: ['gperfil-locais'], queryFn: () => api.get('/trafego/google-perfil/locais', { timeout: 120000 }).then((r) => r.data.data) });
  const escolher = useMutation({
    mutationFn: (l) => api.put('/trafego/google-perfil/local', { conta: l.conta, local: l.local }, { timeout: 600000 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gperfil'] }),
    onError: (e) => alert(erroMsg(e)),
  });
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 text-sm space-y-2">
      <div className="font-semibold text-gray-800">Escolha o perfil da inBase</div>
      {locais.isLoading && <div className="text-gray-500">Buscando os perfis que esse login administra…</div>}
      {locais.error && <div className="text-red-700">{erroMsg(locais.error)}</div>}
      {locais.data && !locais.data.length && <div className="text-gray-600">Nenhum perfil encontrado nesse login do Google. O login conectado precisa ser proprietário ou administrador do perfil.</div>}
      {(locais.data || []).map((l) => (
        <button key={l.local} onClick={() => escolher.mutate(l)} disabled={escolher.isPending}
          className="block w-full text-left border border-gray-200 rounded-lg px-3 py-2 hover:border-primary disabled:opacity-50">
          <strong>{l.titulo}</strong>{l.endereco && <span className="text-gray-500"> · {l.endereco}</span>}
          {!l.verificado && <span className="text-yellow-700"> · perfil não verificado</span>}
          <div className="text-xs text-gray-400">{l.conta_nome}</div>
        </button>
      ))}
      {escolher.isPending && <div className="text-gray-500">Puxando o histórico do perfil (até 18 meses)…</div>}
    </div>
  );
}

function Avaliacao({ a, isAdmin }) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState(null);
  const atualizar = () => qc.invalidateQueries({ queryKey: ['gperfil'] });
  const sugerir = useMutation({ mutationFn: () => api.post(`/trafego/google-perfil/avaliacoes/${a.id}/sugerir`, {}, { timeout: 180000 }).then((r) => r.data.data), onSuccess: (d) => { setTexto(d.sugestao); atualizar(); }, onError: (e) => alert(erroMsg(e)) });
  const responder = useMutation({ mutationFn: (t) => api.post(`/trafego/google-perfil/avaliacoes/${a.id}/responder`, { texto: t }, { timeout: 120000 }), onSuccess: () => { setTexto(null); atualizar(); }, onError: (e) => alert(erroMsg(e)) });
  const rascunho = texto ?? a.sugestao;
  return (
    <div className="border-b border-gray-50 p-3 text-sm space-y-1">
      <div className="flex items-center gap-2"><Estrelas n={a.nota} /><strong className="text-gray-800">{a.autor || 'Anônimo'}</strong>
        <span className="text-xs text-gray-400">{a.criada_em ? new Date(a.criada_em).toLocaleDateString('pt-BR') : ''}</span>
        {!a.resposta && <span className="text-[11px] px-2 py-0.5 rounded bg-yellow-50 text-yellow-700">sem resposta</span>}</div>
      {a.comentario ? <p className="text-gray-700 whitespace-pre-line">{a.comentario}</p> : <p className="text-xs text-gray-400">(só a nota, sem comentário)</p>}
      {a.resposta && <div className="ml-4 pl-3 border-l-2 border-gray-200 text-gray-600 text-xs whitespace-pre-line"><strong>Resposta da inBase:</strong> {a.resposta}</div>}
      {isAdmin && !a.resposta && (
        rascunho != null ? (
          <div className="ml-4 space-y-1">
            <textarea className="w-full border border-gray-200 rounded-lg p-2 text-xs" rows={3} value={rascunho} onChange={(e) => setTexto(e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => responder.mutate(rascunho)} disabled={responder.isPending || !rascunho.trim()} className="bg-primary text-white rounded-lg px-3 py-1 text-xs disabled:opacity-50">{responder.isPending ? 'Publicando…' : 'Aprovar e publicar no Google'}</button>
              <button onClick={() => sugerir.mutate()} disabled={sugerir.isPending} className="border border-gray-200 rounded-lg px-3 py-1 text-xs">{sugerir.isPending ? 'Pensando…' : 'Sugerir outra'}</button>
            </div>
          </div>
        ) : (
          <div className="ml-4 flex gap-2">
            <button onClick={() => sugerir.mutate()} disabled={sugerir.isPending} className="flex items-center gap-1 border border-primary text-primary rounded-lg px-3 py-1 text-xs"><Sparkles size={12} /> {sugerir.isPending ? 'Pensando…' : 'IA sugere resposta'}</button>
            <button onClick={() => setTexto('')} className="border border-gray-200 rounded-lg px-3 py-1 text-xs">Escrever</button>
          </div>
        )
      )}
    </div>
  );
}

function Painel({ isAdmin, liberada }) {
  const [dias, setDias] = useState(28);
  const [filtro, setFiltro] = useState('todas');
  const { data: r } = useQuery({ queryKey: ['gperfil', 'resumo', dias], enabled: liberada, queryFn: () => api.get('/trafego/google-perfil/resumo', { params: { dias } }).then((x) => x.data.data) });
  const avs = useQuery({ queryKey: ['gperfil', 'avaliacoes', filtro], enabled: liberada, queryFn: () => api.get('/trafego/google-perfil/avaliacoes', { params: { filtro } }).then((x) => x.data.data) });
  const a = r?.atual || {}; const b = r?.anterior || {};
  const vazio = !liberada;
  return (
    <div className={`space-y-4 ${vazio ? 'opacity-60 pointer-events-none select-none' : ''}`}>
      <div className="flex gap-2">{[7, 28, 90, 180].map((d) => <button key={d} onClick={() => setDias(d)} className={`px-3 py-1 rounded-full text-xs border ${dias === d ? 'bg-primary text-white border-primary' : 'border-gray-200'}`}>{d} dias</button>)}</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Vistas na Busca" valor={vazio ? null : a.buscas} antes={b.buscas} />
        <Kpi label="Vistas no Maps" valor={vazio ? null : a.mapas} antes={b.mapas} />
        <Kpi label="Ligações" valor={vazio ? null : a.ligacoes} antes={b.ligacoes} />
        <Kpi label="Pedidos de rota" valor={vazio ? null : a.rotas} antes={b.rotas} />
        <Kpi label="Cliques no site" valor={vazio ? null : a.cliques_site} antes={b.cliques_site} />
        <Kpi label="Mensagens" valor={vazio ? null : a.mensagens} antes={b.mensagens} />
      </div>
      {r?.serie?.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={r.serie}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="data" tickFormatter={fmtDia} fontSize={11} />
              <YAxis yAxisId="v" fontSize={11} />
              <YAxis yAxisId="a" orientation="right" fontSize={11} />
              <Tooltip labelFormatter={fmtDia} formatter={(v) => fmtNum(v)} />
              <Line yAxisId="v" type="monotone" dataKey="visualizacoes" name="Visualizações" stroke="#1f5f4a" dot={false} />
              <Line yAxisId="a" type="monotone" dataKey="acoes" name="Ações (ligação, rota, site, mensagem)" stroke="#c9a96e" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-sm">
          <div className="font-semibold text-gray-800 mb-2">Buscas que mostraram o perfil</div>
          {r?.buscas?.length ? (
            <table className="w-full text-xs"><tbody>
              {r.buscas.map((x) => <tr key={x.termo} className="border-b border-gray-50"><td className="py-1">{x.termo}</td><td className="py-1 text-right text-gray-600">{x.valor != null ? fmtNum(x.valor) : `< ${x.menos_de}`}</td></tr>)}
            </tbody></table>
          ) : <p className="text-xs text-gray-500">{vazio ? 'Aparece aqui quando a API for liberada.' : 'Sem termos de busca nos últimos 3 meses.'}</p>}
          {r?.buscas?.[0]?.periodo && <p className="text-[11px] text-gray-400 mt-2">Meses {r.buscas[0].periodo.replace('..', ' a ')}</p>}
        </div>
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-3 flex items-center gap-3 border-b border-gray-100 text-sm">
            <span className="font-semibold text-gray-800">Avaliações</span>
            {r?.avaliacoes?.total ? <span className="flex items-center gap-1 text-xs text-gray-600"><Estrelas n={Math.round(r.avaliacoes.media)} /> {String(r.avaliacoes.media).replace('.', ',')} · {r.avaliacoes.total} avaliações · {r.avaliacoes.sem_resposta} sem resposta</span> : null}
            <select className="ml-auto border border-gray-200 rounded px-2 py-1 text-xs" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
              <option value="todas">todas</option><option value="sem_resposta">sem resposta</option>
            </select>
          </div>
          {(avs.data || []).map((x) => <Avaliacao key={x.id} a={x} isAdmin={isAdmin} />)}
          {!(avs.data || []).length && <p className="p-4 text-center text-xs text-gray-500">{vazio ? 'As avaliações do Google aparecem aqui — a IA sugere a resposta e você aprova antes de publicar.' : 'Nenhuma avaliação.'}</p>}
        </div>
      </div>
    </div>
  );
}

function Info({ st }) {
  const i = st.info || {};
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 text-sm grid md:grid-cols-2 gap-2">
      <div>
        <div className="font-semibold text-gray-800 flex items-center gap-1"><MapPin size={14} /> {st.titulo}</div>
        {i.categoria && <div className="text-xs text-gray-500">{[i.categoria, ...(i.outras_categorias || [])].join(' · ')}</div>}
        {i.endereco && <div className="text-xs text-gray-600 mt-1">{i.endereco}</div>}
        <div className="text-xs text-gray-600">{[i.telefone, i.site].filter(Boolean).join(' · ')}</div>
        <div className="flex gap-3 mt-1 text-xs">
          {i.maps && <a href={i.maps} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1">Ver no Maps <ExternalLink size={11} /></a>}
          {i.nova_avaliacao && <a href={i.nova_avaliacao} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1">Link para pedir avaliação <ExternalLink size={11} /></a>}
        </div>
      </div>
      <div className="text-xs text-gray-600">
        <div className="font-medium text-gray-700 mb-1">Horários</div>
        {i.horarios?.length ? i.horarios.map((h, k) => <div key={k}>{DIA[h.dia] || h.dia}: {h.abre}–{h.fecha}</div>) : <div className="text-gray-400">não informados</div>}
      </div>
    </div>
  );
}

export default function GooglePerfil({ isAdmin }) {
  const qc = useQueryClient();
  const { data: st, isLoading } = useQuery({ queryKey: ['gperfil', 'status'], queryFn: () => api.get('/trafego/google-perfil').then((r) => r.data.data) });
  const checar = useMutation({
    mutationFn: () => api.get('/trafego/google-perfil', { params: { checar: 1 }, timeout: 60000 }).then((r) => r.data.data),
    onSuccess: (d) => { qc.setQueryData(['gperfil', 'status'], d); if (d.api_estado === 'liberada') qc.invalidateQueries({ queryKey: ['gperfil'] }); },
    onError: (e) => alert(erroMsg(e)),
  });
  const sincronizar = useMutation({ mutationFn: () => api.post('/trafego/google-perfil/sincronizar', { dias: 14 }, { timeout: 600000 }), onSuccess: () => qc.invalidateQueries({ queryKey: ['gperfil'] }), onError: (e) => alert(erroMsg(e)) });
  if (isLoading) return <div className="text-gray-500 text-sm">Carregando…</div>;
  const liberada = st?.api_estado === 'liberada';
  const aviso = AVISO[st?.api_estado];

  return (
    <div className="space-y-5">
      {!liberada && aviso && (
        <div className="rounded-xl p-4 text-sm border bg-yellow-50 border-yellow-200 text-yellow-900 space-y-1">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> {aviso.titulo}</div>
          <p className="text-xs">{aviso.texto || st.api_detalhe}</p>
          <div className="flex items-center gap-3 text-xs pt-1">
            <button onClick={() => checar.mutate()} disabled={checar.isPending} className="flex items-center gap-1 border border-yellow-300 bg-white rounded-lg px-3 py-1"><RefreshCw size={12} className={checar.isPending ? 'animate-spin' : ''} /> Verificar de novo</button>
            {st.api_checado_em && <span className="text-yellow-800">Última verificação: {new Date(st.api_checado_em).toLocaleString('pt-BR')}</span>}
          </div>
        </div>
      )}

      {liberada && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={15} /> Perfil da Empresa liberado{st.email ? ` (${st.email})` : ''}</span>
          {st.local && <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending} className="flex items-center gap-1 border border-gray-200 px-3 py-1.5 rounded-lg ml-auto"><RefreshCw size={13} className={sincronizar.isPending ? 'animate-spin' : ''} /> {sincronizar.isPending ? 'Atualizando…' : 'Atualizar dados'}</button>}
          {st.ultimo_sync_erro && <div className="w-full text-xs text-red-600">Última atualização com erro: {st.ultimo_sync_erro}</div>}
        </div>
      )}

      {liberada && !st.local && (isAdmin ? <EscolherLocal /> : <p className="text-sm text-gray-600">Um admin precisa escolher o perfil da inBase.</p>)}
      {liberada && st.local && <Info st={st} />}
      {(!liberada || st.local) && <Painel isAdmin={isAdmin} liberada={liberada && !!st.local} />}
      <p className="text-xs text-gray-500">Atualiza sozinho todo dia às 6h25. O Gestor IA lê o Perfil da Empresa (buscas, ligações, rotas, avaliações) e sugere melhorias. Respostas às avaliações só vão para o Google depois que você aprova.</p>
    </div>
  );
}
